import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { SegmentedControl } from '@mantine/core';

/** Which layout the Documents list renders in. */
type DocumentView = 'grid' | 'list';

interface DocumentViewToggleProps {
  value: DocumentView;
  onChange: (view: DocumentView) => void;
}

/**
 * Grid/list layout switch for the Documents page, styled to match the Flora
 * segmented controls (white indicator on a subtle track).
 *
 * @param {DocumentViewToggleProps} props - Current view and change handler.
 * @returns {JSX.Element} The two-option segmented control.
 */
export function DocumentViewToggle({ value, onChange }: DocumentViewToggleProps) {
  return (
    <SegmentedControl
      value={value}
      onChange={(next) => onChange(next as DocumentView)}
      size="xs"
      aria-label="Document layout"
      data={[
        {
          value: 'grid',
          label: (
            <SegmentLabel>
              <Icon icon="material-symbols:grid-view-outline" width={18} height={18} />
              <VisuallyHidden>Grid view</VisuallyHidden>
            </SegmentLabel>
          ),
        },
        {
          value: 'list',
          label: (
            <SegmentLabel>
              <Icon icon="material-symbols:format-list-bulleted" width={18} height={18} />
              <VisuallyHidden>List view</VisuallyHidden>
            </SegmentLabel>
          ),
        },
      ]}
      styles={{
        root: { background: 'var(--sky-lighter)', borderRadius: 9, padding: 3 },
        label: { color: 'var(--ink-light)', padding: '4px 8px' },
        indicator: { background: 'var(--base-white)', borderRadius: 7, boxShadow: 'var(--box-shadow-small-soft)' },
      }}
    />
  );
}

const SegmentLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export type { DocumentView };
